import React, { useEffect, useRef } from 'react';

import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';

import { getJiraCreateErrorReason } from '../../../common/utils/jira-create-with-rovo-preview-card';

const messages = defineMessages({
	defaultErrorMessage: {
		defaultMessage:
			"Rovo couldn't create your work items. Refresh the page, or try again with a different prompt.",
		description: 'Default error message displayed when an unhandled error occurs.',
		id: 'jiraCreateErrorBoundaryFallback.heading',
	},
	refreshButton: {
		defaultMessage: 'Refresh page',
		description: 'Button label to refresh the page when an error occurs.',
		id: 'jiraCreateErrorBoundaryFallback.refreshButton',
	},
});

const styles = cssMap({
	container: {
		marginBlockStart: token('space.150'),
	},
	iconContainer: {
		marginBlockStart: token('space.025'),
	},
	buttonWrapper: {
		alignSelf: 'start',
	},
});

type Props = {
	error?: Error;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
};

export const JiraCreateErrorBoundaryFallback = ({
	error,
	sendAifcAnalyticsEvent,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const hasFiredViewEvent = useRef(false);

	useEffect(() => {
		if (!hasFiredViewEvent.current) {
			hasFiredViewEvent.current = true;
			sendAifcAnalyticsEvent?.({
				action: 'rendered',
				actionSubject: 'component',
				actionSubjectId: 'createWorkItemsFallback',
				attributes: {
					reason: getJiraCreateErrorReason(error),
					errorName: error?.name,
				},
			});
		}
	}, [sendAifcAnalyticsEvent, error]);

	const handleRefresh = () => {
		window.location.reload();
	};

	return (
		<Stack space="space.150">
			<Inline space="space.075" alignBlock="start" xcss={styles.container}>
				<Box xcss={styles.iconContainer}>
					<StatusErrorIcon color={token('color.icon.danger')} label="" />
				</Box>
				<Text>{formatMessage(messages.defaultErrorMessage)}</Text>
			</Inline>
			<Box xcss={styles.buttonWrapper}>
				<Button onClick={handleRefresh}>{formatMessage(messages.refreshButton)}</Button>
			</Box>
		</Stack>
	);
};
